import React, { useState, useEffect, useRef } from 'react';
import { PlusIcon, CheckChunkierIcon } from '@eventbrite/marmalade';
import { FormattedMessage } from 'react-intl';
import { usePicoXTrack } from '@eventbrite/bsp-sdk/react';
import { useBasicInfo } from '@/src/listings/components/_shared/contexts/BasicInfoContext';
import { useUserData } from '@/src/listings/hooks/useUserData/useUserData';
import { toPrivateEventId } from '@/src/listings/utils/picoEventId';
import {
  PicoXFollowArtistEventNames,
  PICOX_TRACKING_ORIGIN,
  PICOX_SOURCE,
  type PicoXPayloadValue,
} from '@/src/listings/tracking/picoxInteractionsMapper';
import styles from './FollowArtistButton.module.scss';

type FollowArtistButtonProps = {
  artistId: string;
  artistName: string;
};

export const FollowArtistButton = ({ artistId, artistName }: FollowArtistButtonProps) => {
  const [followed, setFollowed] = useState(false);
  const hasFiredShown = useRef(false);
  const trackPicoX = usePicoXTrack();
  const { id: eventId, organizer } = useBasicInfo();
  const { userId, isLoading: isUserDataLoading } = useUserData();

  const buildPayload = (): Record<string, PicoXPayloadValue> => ({
    tracking_origin: PICOX_TRACKING_ORIGIN,
    source: PICOX_SOURCE,
    eb_event_id: toPrivateEventId(eventId),
    artist_id: artistId,
    artist_name: artistName,
    user_id: userId,
    organizer_id: organizer?.id ?? null,
  });

  useEffect(() => {
    if (hasFiredShown.current) return;
    // Wait until user data has loaded so the snapshot records the real user_id
    // (useUserData resolves asynchronously via React Query).
    if (isUserDataLoading) return;
    hasFiredShown.current = true;
    trackPicoX(PicoXFollowArtistEventNames.Shown, buildPayload());
    // buildPayload intentionally excluded — snapshot once user data is resolved
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserDataLoading]);

  const handleClick = () => {
    const next = !followed;
    setFollowed(next);
    trackPicoX(next ? PicoXFollowArtistEventNames.Tapped : PicoXFollowArtistEventNames.Untapped, buildPayload());
  };

  return (
    <button type="button" className={styles.followLink} onClick={handleClick}>
      {followed ? <CheckChunkierIcon /> : <PlusIcon />}
      {followed ? (
        <FormattedMessage id="Following" defaultMessage="Following" />
      ) : (
        <FormattedMessage id="Follow artist" defaultMessage="Follow artist" description="CTA to follow an artist" />
      )}
    </button>
  );
};
